import classNames from 'classnames'
import { ImageUp } from 'lucide-react'
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Typography } from 'src/modules/core/components'
import { formatBrandingMessage, logoSizeInKb } from '../booking-branding-card.domain'
import type { BrandingLogoPickerProps, FilledLogoProps } from './branding-logo-picker.interface'
import classes from './branding-logo-picker.module.scss'

export function BrandingLogoPicker({ logo, error, disabled, onPick, onRemove }: BrandingLogoPickerProps) {
  const { formatMessage } = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const openChooser = () => inputRef.current?.click()

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) onPick(file)
  }
  // Drag events keep preventDefault while disabled, or the browser would navigate to the dropped file.
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragOver(false)
    const file = event.dataTransfer.files?.[0]
    if (file && !disabled) onPick(file)
  }
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragOver(!disabled)
  }

  return (
    <div className={classes.root}>
      <Typography variant="body" className={classes.label}>
        {formatMessage({ id: 'settings.bookingBranding.logo.label' })}
      </Typography>
      <div
        className={classNames(classes.dropArea, { [classes.dragOver]: isDragOver, [classes.filled]: !!logo })}
        data-testid="branding-logo-drop-area"
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          className={classes.fileInput}
          aria-label={formatMessage({ id: 'settings.bookingBranding.logo.label' })}
          disabled={disabled}
          onChange={handleChange}
        />
        {logo ? (
          <FilledLogo logo={logo} disabled={disabled} onReplace={openChooser} onRemove={onRemove} />
        ) : (
          <button type="button" className={classes.emptyButton} disabled={disabled} onClick={openChooser}>
            <ImageUp className={classes.emptyIcon} aria-hidden />
            <span className={classes.emptyTitle}>{formatMessage({ id: 'settings.bookingBranding.logo.empty' })}</span>
            <span className={classes.emptyHint}>
              {formatMessage({ id: 'settings.bookingBranding.logo.emptyHint' })}
            </span>
          </button>
        )}
      </div>
      {error && (
        <Typography variant="body" className={classes.error} role="alert">
          {formatBrandingMessage(error, formatMessage)}
        </Typography>
      )}
    </div>
  )
}

function FilledLogo({ logo, disabled, onReplace, onRemove }: FilledLogoProps) {
  const { formatMessage } = useIntl()
  const src = logo.kind === 'new' ? logo.objectUrl : logo.url

  return (
    <div className={classes.filledRow}>
      <img
        className={classes.thumbnail}
        src={src}
        alt={formatMessage({ id: 'settings.bookingBranding.logo.thumbnailAlt' })}
      />
      <div className={classes.fileDetails}>
        {logo.kind === 'new' && (
          <>
            <span className={classes.fileName}>{logo.file.name}</span>
            <span className={classes.fileMeta}>
              {formatMessage(
                { id: 'settings.bookingBranding.logo.details' },
                { width: logo.width, height: logo.height, size: logoSizeInKb(logo.file.size) },
              )}
            </span>
          </>
        )}
        <div className={classes.fileActions}>
          <button type="button" className={classes.textButton} disabled={disabled} onClick={onReplace}>
            {formatMessage({ id: 'settings.bookingBranding.logo.replace' })}
          </button>
          <button type="button" className={classes.textButton} disabled={disabled} onClick={onRemove}>
            {formatMessage({ id: 'settings.bookingBranding.logo.remove' })}
          </button>
        </div>
      </div>
    </div>
  )
}
